import React from 'react'

// @src imports
import * as SendDelayOptions from 'src/orders/sendDelayOptions'
import { Confirm, DefaultError, Transition } from 'src/chrome'
import { CardPanelView } from 'src/orders/components'
import {
  CampaignDrawer,
  CampaignFooter,
  CampaignHeader,
  CampaignHeaderActions,
  CampaignLineCard,
  CampaignOrders,
  InvoiceModal,
} from 'src/campaigns/components'

import {
  CampaignFragment,
  DetailedOrderFragment,
  getCampaign,
} from 'src/graphql'
import { CampaignRoute } from 'src/campaigns/routes/CampaignRoute'

import {
  useActions,
  useAppcues,
  useCallback,
  useMutations,
  useQueries,
  useReload,
  useState,
} from 'src/hooks'
import CampaignLineAddCard from '../CampaignLine/CampaignLineAddCard'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { EditCampaignCardRoute } from '../../routes/EditCampaignCardRoute'
import { ReplaceCampaignCardRoute } from '../../routes/ReplaceCampaignCardRoute'
import { AddCampaignCardRoute } from '../../routes/AddCampaignCardRoute'
import AddCampaignCard, {
  CardActionsProps,
} from '../AddCampaignCard/AddCampaignCard'
import EditCampaignCard from '../EditCampaignCard/EditCampaignCard'
import ReplaceCampaignCard from '../ReplaceCampaignCard/ReplaceCampaignCard'
import { Text } from '@sendoutcards/quantum-design-ui'

const styles = {
  campaign: {
    minHeight: '100vh',
    paddingBottom: '50px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  ordersContainer: {
    margin: 'auto auto 0',
    display: 'flex',
    flexDirection: 'column' as const,
    borderTop: '1px solid #b4b3b4',
    width: '100%',
    padding: '15px 35px 75px',
    backgroundColor: 'white',
  },
  ordersHeader: {
    margin: '15px 0 0 15px',
  },
  campaignGrid: {
    width: '100%',
    maxWidth: '972px',
    margin: '0 auto',
    paddingBottom: '65px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 324px)',
    justifyContent: 'center',
  },
}

interface Props {
  route: CampaignRoute
}

const Campaign: React.FC<Props> = props => {
  const { route } = props
  const resetRoute = () =>
    actions.openCampaigns(CampaignRoute(route.campaignId))
  const reload = useReload()
  const campaignQuery = getCampaign({ id: route.campaignId })

  const mutations = useMutations()

  const [editableCampaign] = useQueries(campaignQuery)

  // Drawer visibility control
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Modal visibility control
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [isCardPreviewModalOpen, setIsCardPreviewModalOpen] = useState(false)

  const [activeCampaignLine, setActiveCampaignLine] = useState(0)
  const [activeOrder, setActiveOrder] = useState<DetailedOrderFragment>()

  const actions = useActions()

  const selectedCard = editableCampaign.lines[activeCampaignLine]?.card

  useAppcues('-L89ap8oSHf53PF86-N7')

  const handleDrawer = (index: number) => {
    setIsDrawerOpen(true)
    setActiveCampaignLine(index)
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setActiveCampaignLine(0)
  }

  const handleUpdateCampaign = async (campaign: CampaignFragment) => {
    await mutations.updateCampaign({
      campaign: {
        id: campaign.id,
        name: campaign.name,
        lines: campaign.lines?.map(line => {
          return {
            card: line.card?.id,
            giftVariation: line.giftVariation?.id,
            sendDelay: line.sendDelay,
          }
        }),
      },
    })
  }

  const handleResend = useCallback(
    async (order: DetailedOrderFragment) => {
      if (order.lines.length > 0) {
        const {
          createOrder: { order: createdOrder },
        } = await mutations.createOrder({
          order: {
            lines: order.lines?.map(line => ({
              card: line.card?.id,
              giftVariation: line.giftVariation?.id,
              sendDelay: line.sendDelay,
            })),
          },
        })
        actions.openOrder(createdOrder.id)
      }
    },
    [actions, mutations],
  )

  const handleUpdatePaperType = () => {
    reload(campaignQuery)
  }

  const handleShowCardPreview = (index: number) => {
    setActiveCampaignLine(index)
    setIsCardPreviewModalOpen(true)
  }

  const handleCloseCardPreview = () => {
    setActiveCampaignLine(0)
    setIsCardPreviewModalOpen(false)
  }

  const showInvoice = (order: DetailedOrderFragment) => {
    setActiveOrder(order)
    setIsInvoiceModalOpen(true)
  }

  const closeInvoiceModal = () => {
    setActiveOrder(undefined)
    setIsInvoiceModalOpen(false)
  }

  const cardActionProps: CardActionsProps = {
    editableCampaign: editableCampaign,
    campaignRoute: route,
    route: route.subroute as
      | AddCampaignCardRoute
      | EditCampaignCardRoute
      | ReplaceCampaignCardRoute,
    resetRoute: resetRoute,
  }

  const handleAddCard = () => {
    actions.openCampaigns(
      CampaignRoute(route.campaignId, AddCampaignCardRoute()),
    )
  }

  const handleEditCard = (cardId: string) => {
    actions.openCampaigns(
      CampaignRoute(route.campaignId, EditCampaignCardRoute(cardId)),
    )
  }

  const handlePickReplacementCard = (cardId: string) => {
    actions.openCampaigns(
      CampaignRoute(route.campaignId, ReplaceCampaignCardRoute(cardId)),
    )
  }

  switch (route.subroute?.path) {
    // pick a new card to add
    case AddCampaignCardRoute.path:
      return <AddCampaignCard {...cardActionProps} />

    // edit an existing card
    case EditCampaignCardRoute.path:
      return <EditCampaignCard {...cardActionProps} />

    // pick a card to replace another one
    case ReplaceCampaignCardRoute.path:
      return (
        <ReplaceCampaignCard
          {...cardActionProps}
          reload={() => reload(campaignQuery)}
        />
      )

    case undefined:
      return (
        <div>
          {editableCampaign && (
            <div css={styles.campaign}>
              <CampaignHeader
                campaignId={editableCampaign.id}
                name={editableCampaign.name}
              />
              <CampaignHeaderActions
                hasValidSequence={SendDelayOptions.validateSequence(
                  editableCampaign.lines,
                )}
                campaignId={editableCampaign.id}
                campaignLines={editableCampaign.lines}
                campaignName={editableCampaign.name}
                isCampaignShareable={editableCampaign.isShareable}
              />
              <div css={styles.campaignGrid}>
                {editableCampaign.lines.map((line, index) => {
                  return (
                    <CampaignLineCard
                      key={line.card?.id}
                      isActive={activeCampaignLine === index}
                      line={line}
                      title={line.card?.sendableCard?.title}
                      buttons={{
                        primaryButton: {
                          label: 'View Card',
                          onClick: () => handleShowCardPreview(index),
                        },
                        secondaryButton: {
                          label: 'Edit',
                          onClick: () => handleDrawer(index),
                        },
                      }}
                    />
                  )
                })}
                <CampaignLineAddCard onAddCard={handleAddCard} />
                {isDrawerOpen && (
                  <CampaignDrawer
                    campaign={editableCampaign}
                    lineIndex={activeCampaignLine}
                    onClose={closeDrawer}
                    onEditCard={handleEditCard}
                    onReplaceCard={handlePickReplacementCard}
                    onUpdatePaperType={handleUpdatePaperType}
                    onUpdateCampaign={handleUpdateCampaign}
                  />
                )}
                {isCardPreviewModalOpen && selectedCard && (
                  <Confirm
                    title={'Card Preview'}
                    confirmTitle={'Edit'}
                    declineTitle={'Close'}
                    onConfirm={() => handleEditCard(selectedCard.id)}
                    onDecline={() => handleCloseCardPreview()}
                    childType={'image'}
                    isModalEnabled={true}
                  >
                    <CardPanelView
                      card={{
                        ...selectedCard,
                        panels: selectedCard.minimalPanels,
                      }}
                    />
                  </Confirm>
                )}
              </div>
              <div css={styles.ordersContainer}>
                <div>
                  <div css={styles.ordersHeader}>
                    <Text type="subtitle">Orders</Text>
                  </div>
                  <CampaignOrders
                    campaignId={route.campaignId}
                    onResend={handleResend}
                    onViewInvoice={showInvoice}
                    openOrder={actions.openOrder}
                  />
                </div>
              </div>
              <CampaignFooter
                cards={editableCampaign.lines.length}
                isSaved={true}
                isSaving={false}
                isInvalid={editableCampaign.lines.length < 1}
                editableCampaignId={editableCampaign.id}
                name={editableCampaign.name}
                onSave={() => handleUpdateCampaign(editableCampaign)}
              />
              <InvoiceModal
                isOpen={isInvoiceModalOpen}
                onClose={closeInvoiceModal}
                order={activeOrder}
              />
            </div>
          )}
        </div>
      )
  }
}

export default suspenseBoundary({
  component: Campaign,
  unresolved: <Transition message={'Loading...'} />,
  failure: DefaultError,
})
